import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { Field, FormSpy } from "react-final-form";
import { FaSpinner } from "react-icons/fa";
import { validator } from "./validation";
import FieldDisabled from "./FieldDisabled";
import "./UI/styles/Groups.scss";

import { useRecoilValue } from "recoil";
import { tableErrorsSelector } from "./Atoms/Table/selectors";

import Label from "./UI/Label";
import ClearBtn from "./UI/ClearBtn";
import Legend from "./UI/Legend";
import PageTitle from "./UI/PageTitle";
import ButtonGroup from "./UI/ButtonGroup";
import Button from "./UI/Button";
import RequiredAsterisk from "./UI/RequiredAsterisk";
import Tooltip from "./UI/FormTooltip";
import PageInfo from "./UI/PageInfo";
import PageAlert from "./UI/PageAlert";
import Divider from "./UI/Divider";

import FieldError from "./FieldError";
import fieldParser from "./FieldParser";
import fieldFormatter from "./FieldFormatter";
import Prefiller from "./Prefiller";
import ModalPrefiller from "./Prefiller/ModalPrefiller";
import TableFormGenerator from "./TableFormGenerator";
import PageErrors from "./UI/PageErrors";
import InlineModal from "./InlineModal";
import FieldResolver from "./FieldResolver";
import FieldArrayGenerator from "./FieldArrayGenerator";
import Condition from "./Conditionals/Condition";
import FEINValidation from "./FEINValidation";
import ProfileLinkingTable from "../ProfileLinkingTable";

// Commented this out to disable different styling for
// V2 790A toggling of new forms for 02/13/2023 revisions
const conditionalStyles = {
  // background: "#f7f7f7",
  // border: "1px solid #C3C5C9",
  // padding: "0px 16px 28px 21px",
  width: "100%",
  marginTop: "1rem",
};

const warningStyles = {
  backgroundColor: "#e9e9e9",
  border: "1px solid #d2d3d6",
  padding: "28px 16px 28px 21px",
  width: "100%",
  marginTop: "1rem",
};

const checkboxStyleObj = {
  WebkitColumnCount: "2" /* Chrome, Safari, Opera */,
  MozColumnCount: "2" /* Firefox */,
  columnCount: "2",
};

function Page(props) {
  let [currentErrors, setCurrentErrors] = useState([]);
  const [fieldArrayExtraInfo, setFieldArrayExtraInfo] = useState({
    rowDisabled: false,
    isInitialStateValue: true,
  });

  const fieldWarning = (field, formApi) => {
    if (field.warnings) {
      return field.warnings.map((warning) => (
        <Condition
          when={warning.isVisible.when}
          is={warning.isVisible.is}
          operator={warning.isVisible.operator}
          formApi={formApi}
        >
          <div style={warningStyles}>{parse(warning.text)}</div>
        </Condition>
      ));
    } else if (field.warning) {
      return field.warning.isVisible ? (
        <Condition
          when={field.warning.isVisible.when}
          is={field.warning.isVisible.is}
          operator={field.warning.isVisible.operator}
          array={field.warning.isVisible.array}
          property={field.warning.isVisible.property}
          formApi={formApi}
        >
          <div style={warningStyles}>{parse(field.warning.text)}</div>
        </Condition>
      ) : null;
    }
  };

  const updateCurrentErrors = (errors) => {
    let updated = JSON.stringify(errors) !== JSON.stringify(currentErrors);

    if (updated) {
      setCurrentErrors(errors);
    }
  };

  const renderInfo = (info) => <PageInfo {...info} />;

  const renderInlineModal = (section, formApi, formValues, counties) => {
    const { reuseMetadata } = props;
    return (
      <InlineModal
        key={section.machine_name}
        section={section}
        formApi={formApi}
        formValues={formValues}
        counties={counties}
        disabledViaReuse={
          !!reuseMetadata?.disableSections &&
          !!reuseMetadata?.sections?.includes(props.uiSchema?.name)
        }
      />
    );
  };
  const renderAlert = (alert) => <PageAlert {...alert} formApi={formApi} />;

  const renderTitle = (title) => (
    <PageTitle {...title} formRef={props.formRef} />
  );

  const renderField = (field, index, machine_name, show_error = true) => {
    const {
      inputResolver,
      returningToPage,
      returningToForm,
      appId,
      profiles,
      counties,
      formApi,
      formValues,
      history,
      page_index,
      disableValidation,
      form_ui,
      form_ui_name,
      uiSchema,
      addPageVisited,
      addPageErrored,
      removePageVisited,
      removePageErrored,
      goToStep,
      reuseMetadata,
    } = props;

    // components with the same key weren't updating because keys were not unique
    let key = "";
    key += field && field.code ? field.code : "";
    key += uiSchema && uiSchema.prefix ? uiSchema.prefix : "";
    key += index ? index : "";
    key += machine_name ? machine_name : "";
    key += page_index ? page_index : "";
    key += page_index ? page_index : "";

    let input_resolver = inputResolver || FieldResolver;
    let component = input_resolver(field.widget) || "input";

    let schemaProps = { ...field };
    let showRequired = field.validate && field.validate[0] === "required";
    let disabled = { background: "#D3D3D3" };
    let isDisabled = false;

    if (field.disabled && field.disableWhenField !== undefined) {
      if (typeof field.disableWhenField === "string") {
        isDisabled = FieldDisabled(
          field.disabled,
          field.disableWhenField,
          formApi
        );
      } else if (typeof field.disableWhenField === "function") {
        isDisabled = field.disableWhenField(formApi.getState().values);
      } else {
        isDisabled = FieldDisabled(
          field.disabled,
          field.disableWhenField.when,
          formApi,
          undefined,
          field.disableWhenField.is,
          field.disableWhenField.type
        );
      }
    } else {
      isDisabled = field.disabled;
    }

    if (!!fieldArrayExtraInfo.rowDisabled) {
      isDisabled = !!field.disabledOnEdit;
    }
    if (
      !!reuseMetadata?.disableSections &&
      !!reuseMetadata?.sections?.includes(uiSchema.name)
    ) {
      isDisabled = true;
    }

    return (
      <Fragment key={key}>
        <div className={`usa-form-group`} style={field.style} key={key}>
          {field.label && (
            <Label
              hidden={field.hidden || field.labelHidden}
              htmlFor={machine_name}
            >
              {field.code && <span>{`${field.code}. `}</span>}
              <span
                dangerouslySetInnerHTML={{
                  __html: field.label,
                }}
              />
              <RequiredAsterisk
                show={showRequired || schemaProps.required === true}
              />
              {field.tooltip && <Tooltip content={field.tooltip} />}
              {field.clearBtn && (
                <ClearBtn
                  clearBtn={field.clearBtn}
                  machine_name={machine_name}
                  formApi={formApi}
                />
              )}
            </Label>
          )}
          <Field
            key={key}
            id={machine_name}
            name={machine_name}
            validate={
              disableValidation
                ? undefined
                : (values, allValues) =>
                    field.validate
                      ? validator(
                          field.validate,
                          allValues,
                          null,
                          counties
                        )(values)
                      : undefined
            }
            form_ui_name={form_ui_name}
            formApi={formApi}
            formValues={formValues}
            history={history}
            addPageVisited={addPageVisited}
            addPageErrored={addPageErrored}
            page_index={page_index}
            removePageVisited={removePageVisited}
            removePageErrored={removePageErrored}
            parse={
              field.parse
                ? fieldParser(field.parse, schemaProps, formApi)
                : undefined
            }
            format={
              field.format
                ? fieldFormatter(field.format, schemaProps, formApi)
                : undefined
            }
            component={component}
            type={field.widget}
            disabled={isDisabled}
            disabledViaReuse={
              !!reuseMetadata?.disableSections &&
              !!reuseMetadata?.sections?.includes(uiSchema.name)
            }
            disabledOnEdit={
              !!fieldArrayExtraInfo.rowDisabled && !!field.disabledOnEdit
            }
            fieldArrayExtraInfo={fieldArrayExtraInfo}
            setFieldArrayExtraInfo={setFieldArrayExtraInfo}
            style={field.disabled ? disabled : undefined}
            schemaProps={schemaProps}
            value={
              field.value !== undefined
                ? field.value
                : field.choice !== undefined
                ? field.choice
                : undefined
            } // if field has both value and choice, prefer value
            maxLength={field.maxLength}
            showRequired={showRequired}
            appId={appId}
            hidden={field.hidden}
            inputHidden={field?.inputHidden}
            profiles={profiles}
            counties={counties}
            inputType={field.inputType}
            form_ui={form_ui}
            goToStep={goToStep}
            currentErrors={currentErrors}
            returningToForm={returningToForm}
            returningToPage={returningToPage}
          />
          {(show_error !== false || field.widget === "wage") && (
            <FieldError
              field_name={machine_name}
              returningToPage={returningToPage}
              returningToForm={returningToForm}
              currentErrors={currentErrors}
            />
          )}
        </div>
      </Fragment>
    );
  };

  const shouldShowClearBtn = (hideSchema, formApi) => {
    const { when, is } = hideSchema;
    return formApi.getState().values[when] !== is;
  };

  const renderFieldGroup = (
    group,
    index,
    formApi,
    returningToPage,
    returningToForm
  ) => {
    const showClearBtn = group.clearBtn?.hide
      ? shouldShowClearBtn(group.clearBtn.hide, formApi)
      : true;
    return (
      // condition around the form group
      <Condition
        when={(group.isVisible && group.isVisible.when) || undefined}
        is={(group.isVisible && group.isVisible.is) || undefined}
        operator={(group.isVisible && group.isVisible.operator) || undefined}
        key={index}
        formApi={formApi}
      >
        <div
          className={`usa-form-group`}
          style={
            group.hidden
              ? { display: "none" }
              : group.isVisible
              ? conditionalStyles
              : undefined
          }
        >
          {group.name ? <Legend>{group.name}</Legend> : ""}
          <Label htmlFor={group.machine_name}>
            {group.code && <span>{`${group.code}. `}</span>}
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(group.label, {
                  ADD_ATTR: ["target"],
                }),
              }}
            />
            {group.required && group.required !== undefined && (
              <RequiredAsterisk show={true} />
            )}
            {group.tooltip && <Tooltip content={group.tooltip} />}{" "}
            {group.clearBtn && showClearBtn && (
              <ClearBtn
                clearBtn={group.clearBtn}
                machine_name={group.machine_name}
                formApi={formApi}
              />
            )}
          </Label>

          <div
            className={`${
              group &&
              group.fields &&
              group.fields[0] &&
              group.fields[0].widget === "anticipated_date"
                ? `anticipated_date ${group.className || ""}`
                : `${group.className || ""}`
            }`}
            style={group.columns ? checkboxStyleObj : undefined}
          >
            {group.fields.map((f, index) => {
              if (f.isGroup) {
                return renderFieldGroup(
                  f.isGroup,
                  null,
                  formApi,
                  returningToPage,
                  returningToForm
                ); // recursive call
              } else {
                return (
                  // condition around individual fields within the form group
                  <Condition
                    when={(f.isVisible && f.isVisible.when) || undefined}
                    is={(f.isVisible && f.isVisible.is) || undefined}
                    operator={
                      (f.isVisible && f.isVisible.operator) || undefined
                    }
                    formApi={formApi}
                  >
                    {f.widget !== "anticipated_date" ? (
                      <Fragment>
                        {renderField(
                          f,
                          index,
                          f.machine_name || group.machine_name,
                          (group.showIndividualFieldError !== undefined &&
                            group.showIndividualFieldError) ||
                            false // if prop is present, otherwise assume error at group level
                        )}
                        {/* To be tested */}
                        {f.machine_name === "c12_emp_fein" &&
                        f.feinValidation ? (
                          <FEINValidation
                            formApi={formApi}
                            machine_name={f.machine_name}
                          />
                        ) : (
                          fieldWarning(f, formApi)
                        )}
                      </Fragment>
                    ) : f.machine_name.toLowerCase().indexOf("total") < 0 ? (
                      renderField(
                        f,
                        index,
                        f.machine_name || group.machine_name,
                        (group.showIndividualFieldError !== undefined &&
                          group.showIndividualFieldError) ||
                          false // if prop is present, otherwise assume error at group level
                      )
                    ) : null}
                  </Condition>
                );
              }
            })}
          </div>

          {group.fields[0].widget === "anticipated_date" ? (
            <div className={"anticipated_date_total"}>
              {renderField(
                group.fields[7],
                7,
                group.fields[7].machine_name,
                false
              )}
            </div>
          ) : null}

          <FieldError
            field_name={group.machine_name}
            returningToPage={returningToPage}
            returningToForm={returningToForm}
            currentErrors={currentErrors}
          />
          <div>{fieldWarning(group, formApi)}</div>
        </div>
      </Condition>
    );
  };

  // render entities (fields or groups of fields)
  const renderFields = (fields, _, form_ui_name) => {
    let { returningToPage, returningToForm, formApi } = props;

    if (fields && fields != undefined) {
      return fields.map((field, index) => {
        // not a field group, just render a field with condition wrapper
        if (!field.isGroup) {
          return (
            <div className={`usa-form-group`} key={index}>
              <Condition
                when={(field.isVisible && field.isVisible.when) || undefined}
                is={(field.isVisible && field.isVisible.is) || undefined}
                operator={
                  (field.isVisible && field.isVisible.operator) || undefined
                }
                formApi={formApi || undefined}
              >
                <div
                  style={
                    field.isVisible && field.isVisible.hideStyles
                      ? undefined
                      : field.isVisible
                      ? conditionalStyles
                      : undefined
                  }
                >
                  {renderField(
                    field,
                    index,
                    field.machine_name !== undefined ? field.machine_name : "_",
                    null
                  )}
                  {field.machine_name === "c12_emp_fein" &&
                  field.feinValidation ? (
                    <FEINValidation
                      formApi={formApi}
                      machine_name={field.machine_name}
                    />
                  ) : (
                    fieldWarning(field, formApi)
                  )}
                </div>
              </Condition>
            </div>
          );
        } else {
          // render a field "group" with condition wrapper
          let group = field.isGroup;
          let showIndividualFieldError = true;
          return renderFieldGroup(
            group,
            index,
            formApi,
            returningToPage,
            returningToForm
          );
        }
      });
    } else return "";
  };

  const renderForm = (uiSchema, pageIndex, formApi, _, form_ui_name) => {
    const sectionStyle = (color) => {
      let styleobj = {
        padding: "0 20px 30px 20px",
        margin: "40px -20px 20px -20px",
      };
      switch (color) {
        case "blue":
          styleobj["backgroundColor"] = "#E1F3F8";
          styleobj["border"] = "1px solid #02BFE7";
          return styleobj;
        case "yellow":
          styleobj["backgroundColor"] = "#FFF1D2";
          styleobj["border"] = "1px solid #FEC651";
          return styleobj;
        case "grey":
          styleobj["backgroundColor"] = "#F7F7F7";
          styleobj["border"] = "1px solid #E2E2E2";
          return styleobj;
        default:
      }
      return undefined;
    };
    const {
      returningToPage,
      returningToForm,
      formValues,
      disableValidation,
      counties,
    } = props;

    if (uiSchema) {
      return uiSchema.sections.map((section, index) => {
        return (
          <Condition
            key={index}
            when={(section.isVisible && section.isVisible.when) || undefined}
            is={(section.isVisible && section.isVisible.is) || undefined}
            operator={
              (section.isVisible && section.isVisible.operator) || undefined
            }
            formApi={formApi}
          >
            <fieldset className={`usa-fieldset`} key={index}>
              <div style={sectionStyle(section.coloring)}>
                {section.name ? <Legend>{section.name}</Legend> : ""}
                {section.sectionInfo && (
                  <>
                    <br></br>
                    {renderInfo(section.sectionInfo)}
                  </>
                )}
                {section.isTableInput ? (
                  <TableFormGenerator
                    returningToPage={returningToPage}
                    returningToForm={returningToForm}
                    currentErrors={currentErrors}
                    section={section}
                    formApi={formApi}
                  />
                ) : section.isModal ? (
                  renderInlineModal(section, formApi, formValues, counties)
                ) : section.isProfileLinkingTable ? (
                  <ProfileLinkingTable
                    type={section.type}
                    formApi={formApi}
                    linkingType={section.linkingType}
                    searchType={section.searchType}
                    addButtonText={section.addButtonText}
                  />
                ) : section.isFieldArray ? (
                  <FieldArrayGenerator
                    section={section}
                    formApi={formApi}
                    formValues={formValues}
                    currentErrors={currentErrors}
                    setFieldArrayExtraInfo={setFieldArrayExtraInfo}
                    validate={
                      disableValidation
                        ? undefined
                        : (values, allValues) =>
                            section.fieldArray.validate
                              ? validator(
                                  section.fieldArray.validate,
                                  allValues,
                                  undefined,
                                  counties
                                )(values)
                              : undefined
                    }
                  >
                    {renderForm(
                      section.fieldArray.page,
                      null,
                      formApi,
                      counties
                    )}
                  </FieldArrayGenerator>
                ) : (
                  renderFields(section.fields, formApi, form_ui_name)
                )}
              </div>
            </fieldset>
            {section.divider !== "hide" && <Divider />}
          </Condition>
        );
      });
    } else return "";
  };

  const {
    isLastPage,
    uiSchema,
    uiSchema_lookup,
    pristine,
    invalid,
    profiles,
    counties,
    formName,
    formApi,
    multiPage,
    active,
    returningToPage,
    page_index,
    form_ui_name,
    closeModal,
  } = props;
  let tableErrors = useRecoilValue(tableErrorsSelector);
  const { name, prefill, modalPrefill } = props.uiSchema;
  let [singleSubmitting, setSingleSubmitting] = useState();
  //REFACTOR: once H2B is rewritten using simple form, remove the second state in the || below
  return (
    <div style={!active ? { display: "none" } : undefined}>
      {prefill && profiles.length > 0 ? (
        <Prefiller
          schema={prefill.schema}
          profiles={profiles}
          formDetailName={formName}
          formApi={formApi}
          {...prefill}
        />
      ) : (
        ""
      )}
      {modalPrefill && profiles.length > 0 ? (
        <Condition
          when={
            (modalPrefill.isVisible && modalPrefill.isVisible.when) || undefined
          }
          is={
            (modalPrefill.isVisible && modalPrefill.isVisible.is) || undefined
          }
          operator={
            (modalPrefill.isVisible && modalPrefill.isVisible.operator) ||
            undefined
          }
          formApi={formApi}
        >
          <ModalPrefiller
            schema={modalPrefill.schema}
            profiles={profiles}
            counties={counties}
            formDetailName={formName}
            formApi={formApi}
            key={modalPrefill.type}
            {...modalPrefill}
          />
        </Condition>
      ) : (
        ""
      )}
      {
        // Old Schemas contain "name" (no coloring) as the key value
        // New Schemas contain "pageTitle" (with coloring) as the key value
        // Should work towards changing the old schemas to keep everything consistent
        name ? (
          <PageTitle formRef={props.formRef}>{name}</PageTitle>
        ) : (
          uiSchema.pageTitle && renderTitle(uiSchema.pageTitle)
        )
      }
      {returningToPage && !isLastPage && (
        <FormSpy subscription={{ errors: true }}>
          {({ errors }) => {
            errors = { ...errors, ...tableErrors };
            updateCurrentErrors(errors);
            return (
              <div className="margin-top-5">
                <PageErrors
                  errors={errors}
                  uiSchema_lookup={uiSchema_lookup}
                  page_number={page_index}
                />
              </div>
            );
          }}
        </FormSpy>
      )}

      {/* Will return page info (the grey box) that meets the condition */}
      {uiSchema.pageInfo && (
        <Condition
          when={
            (uiSchema.pageInfo.isVisible && uiSchema.pageInfo.isVisible.when) ||
            undefined
          }
          is={
            (uiSchema.pageInfo.isVisible && uiSchema.pageInfo.isVisible.is) ||
            undefined
          }
          operator={
            (uiSchema.pageInfo.isVisible &&
              uiSchema.pageInfo.isVisible.operator) ||
            undefined
          }
          formApi={formApi}
        >
          {renderInfo(uiSchema.pageInfo)}
        </Condition>
      )}

      {/* Will return all pageAlert that meets the condition */}
      {uiSchema.pageAlert
        ? uiSchema.pageAlert.alerts.map((alert) => {
            return (
              <Condition
                when={(alert.isVisible && alert.isVisible.when) || undefined}
                is={(alert.isVisible && alert.isVisible.is) || undefined}
                operator={
                  (alert.isVisible && alert.isVisible.operator) || undefined
                }
                formApi={formApi}
                modal={(alert.isVisible && alert.isVisible.modal) || undefined}
              >
                {renderAlert(alert)}
              </Condition>
            );
          })
        : null}

      {renderForm(uiSchema, page_index, formApi, counties, form_ui_name)}

      {!multiPage && (
        <ButtonGroup>
          <Button
            coloring="cancel"
            type="button"
            onClick={(e) => {
              closeModal();
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={pristine || invalid}
            coloring="blue"
            type="submit"
            onClick={(e) => {
              setSingleSubmitting(true);
            }}
          >
            {singleSubmitting ? <FaSpinner className="icon-spin" /> : ""} Submit
          </Button>
        </ButtonGroup>
      )}
    </div>
  );
}

Page.propTypes = {
  active: PropTypes.bool,
  multiPage: PropTypes.bool,
};

Page.defaultProps = {
  active: true,
  multiPage: false,
};

export default Page;
